import { useState, useCallback, useEffect, useRef } from 'react';
import {
  IDocument,
  IDocumentListResponse,
  IDocumentFilters,
  IDocumentStats,
  IQueryResponse,
  IAskRequest,
  IAskJob,
  IRetrieveRequest,
  IRetrieveResponse,
  IUploadResponse,
  IQueryHistoryItem,
  IChunk,
} from '../Types/Docs';
import {
  getDocuments,
  getDocument,
  uploadDocument,
  deleteDocument,
  downloadDocument,
  updateDocument,
  createAskJob,
  getAskJob,
  pollAskJob,
  mapAskJobToQueryResult,
  retrieveChunks,
  getDocumentSummary,
} from '../Utils/docsApi';
import { QUERY_HISTORY_KEY, MAX_QUERY_HISTORY, ASK_TIMEOUT_MS } from '../Containers/Docs/constants';

// Document hooks
export const useDocuments = (initialFilters: IDocumentFilters = {}, autoFetch: boolean = true) => {
  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchDocuments = useCallback(async (filters: IDocumentFilters = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getDocuments({ ...initialFilters, ...filters });
      setDocuments(response.data || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch documents');
      setDocuments([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [initialFilters]);

  // Auto-fetch once on mount if enabled
  useEffect(() => {
    if (autoFetch && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchDocuments();
    }
    // Only run once on mount when autoFetch is true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch]);

  return {
    documents,
    total,
    isLoading,
    error,
    refetch: fetchDocuments,
  };
};

export const useDocument = (id: string | null) => {
  const [document, setDocument] = useState<IDocument | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previousIdRef = useRef<string | null>(null);

  const fetchDocument = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const doc = await getDocument(id);
      setDocument(doc);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch document');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // Auto-fetch when id changes
  useEffect(() => {
    if (id && id !== previousIdRef.current) {
      previousIdRef.current = id;
      fetchDocument();
    } else if (!id) {
      // Clear document when id becomes null
      setDocument(null);
      previousIdRef.current = null;
    }
  }, [id, fetchDocument]);

  return {
    document,
    isLoading,
    error,
    refetch: fetchDocument,
  };
};

export const useUploadDocument = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadedDoc, setUploadedDoc] = useState<IUploadResponse | null>(null);

  const upload = useCallback(async (file: File, categories?: string | string[]) => {
    setIsUploading(true);
    setProgress(0);
    setError(null);
    setSuccess(false);
    setUploadedDoc(null);

    try {
      const response = await uploadDocument(file, (prog) => {
        setProgress(prog);
      }, categories);
      setUploadedDoc(response);
      setSuccess(true);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Upload failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setProgress(0);
    setError(null);
    setSuccess(false);
    setUploadedDoc(null);
  }, []);

  return {
    upload,
    isUploading,
    progress,
    error,
    success,
    uploadedDoc,
    reset,
  };
};

export const useDeleteDocument = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteDoc = useCallback(async (id: string) => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteDocument(id);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Delete failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    deleteDoc,
    isDeleting,
    error,
  };
};

const getAskErrorMessage = (err: any): string => {
  if (err?.response?.status === 503) {
    return 'Cursor agent unavailable';
  }
  if (err?.message === 'Ask cancelled') {
    return 'Ask cancelled';
  }
  if (err?.message === 'Ask timed out waiting for agent') {
    return 'Ask timed out waiting for agent';
  }
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Ask failed';
};

// Ask hooks (async /ask with polling)
export const useAsk = () => {
  const [job, setJob] = useState<IAskJob | null>(null);
  const [result, setResult] = useState<IQueryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const processTerminalJob = useCallback((terminalJob: IAskJob): IQueryResponse => {
    setJob(terminalJob);
    if (terminalJob.status === 'done') {
      const mappedResult = mapAskJobToQueryResult(terminalJob);
      setResult(mappedResult);
      setError(null);
      return mappedResult;
    }

    const message = terminalJob.error || 'Cursor agent unavailable';
    setError(message);
    throw new Error(message);
  }, []);

  const runAskPolling = useCallback(
    async (
      jobId: string,
      runId: number,
      controller: AbortController,
      initialJob?: IAskJob
    ): Promise<IQueryResponse> => {
      if (initialJob) {
        setJob(initialJob);
      }

      if (initialJob?.status === 'done' || initialJob?.status === 'failed') {
        if (runId !== runIdRef.current) {
          throw new Error('Ask cancelled');
        }
        return processTerminalJob(initialJob);
      }

      const terminalJob = await pollAskJob(jobId, {
        signal: controller.signal,
        timeoutMs: ASK_TIMEOUT_MS,
        onUpdate: (updatedJob) => {
          if (runId === runIdRef.current) {
            setJob(updatedJob);
          }
        },
      });

      if (runId !== runIdRef.current) {
        throw new Error('Ask cancelled');
      }

      return processTerminalJob(terminalJob);
    },
    [processTerminalJob]
  );

  const startAsk = useCallback(
    async (
      request: IAskRequest,
      opts?: { onJobCreated?: (jobId: string) => void }
    ): Promise<IQueryResponse> => {
      const runId = ++runIdRef.current;
      cancel();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setError(null);
      setResult(null);
      setJob(null);

      try {
        const { jobId } = await createAskJob(request);
        if (runId !== runIdRef.current) {
          throw new Error('Ask cancelled');
        }

        opts?.onJobCreated?.(jobId);
        setJob({
          jobId,
          status: 'queued',
          logs: [],
        });

        return await runAskPolling(jobId, runId, controller);
      } catch (err: any) {
        if (runId !== runIdRef.current || err?.message === 'Ask cancelled') {
          throw err;
        }
        const errorMessage = getAskErrorMessage(err);
        setError(errorMessage);
        throw err;
      } finally {
        if (runId === runIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [cancel, runAskPolling]
  );

  const resumeAsk = useCallback(
    async (jobId: string): Promise<IQueryResponse> => {
      const runId = ++runIdRef.current;
      cancel();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setError(null);
      setResult(null);
      setJob(null);

      try {
        const initialJob = await getAskJob(jobId);
        if (runId !== runIdRef.current) {
          throw new Error('Ask cancelled');
        }

        return await runAskPolling(jobId, runId, controller, initialJob);
      } catch (err: any) {
        if (runId !== runIdRef.current || err?.message === 'Ask cancelled') {
          throw err;
        }
        const errorMessage = getAskErrorMessage(err);
        setError(errorMessage);
        throw err;
      } finally {
        if (runId === runIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [cancel, runAskPolling]
  );

  const reset = useCallback(() => {
    cancel();
    setJob(null);
    setResult(null);
    setError(null);
  }, [cancel]);

  const restoreResult = useCallback((response: IQueryResponse, restoredJob?: IAskJob | null) => {
    setResult(response);
    setJob(restoredJob ?? null);
    setError(null);
  }, []);

  useEffect(() => () => cancel(), [cancel]);

  return {
    startAsk,
    resumeAsk,
    job,
    result,
    isLoading,
    error,
    reset,
    restoreResult,
    cancel,
  };
};

export const useRetrieve = () => {
  const [chunks, setChunks] = useState<IChunk[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retrieve = useCallback(async (request: IRetrieveRequest) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await retrieveChunks(request);
      setChunks(response.chunks);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Retrieve failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setChunks([]);
    setError(null);
  }, []);

  return {
    retrieve,
    chunks,
    isLoading,
    error,
    reset,
  };
};

export const useDocumentSummary = () => {
  const [summary, setSummary] = useState<IDocumentStats>({
    total: 0,
    ready: 0,
    processing: 0,
    failed: 0,
    bySource: {},
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getDocumentSummary();
      setSummary(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch document summary');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summary, isLoading, error, refetch: fetchSummary };
};

// Query history hook (localStorage)
export const useQueryHistory = () => {
  const getHistory = useCallback((): IQueryHistoryItem[] => {
    try {
      const stored = localStorage.getItem(QUERY_HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const addToHistory = useCallback((item: Omit<IQueryHistoryItem, 'id' | 'timestamp'>) => {
    try {
      const history = getHistory();
      const newItem: IQueryHistoryItem = {
        ...item,
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
      };
      
      // Add to beginning and limit to MAX_QUERY_HISTORY
      const updated = [newItem, ...history].slice(0, MAX_QUERY_HISTORY);
      localStorage.setItem(QUERY_HISTORY_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save query history:', error);
    }
  }, [getHistory]);

  const clearHistory = useCallback(() => {
    try {
      localStorage.removeItem(QUERY_HISTORY_KEY);
    } catch (error) {
      console.error('Failed to clear query history:', error);
    }
  }, []);

  return {
    history: getHistory(),
    addToHistory,
    clearHistory,
  };
};
